import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link } from "@mui/material";
import axios from "axios";
import Button from "components/shared-ui/button";
import { InfoDialog } from "components/shared-ui/dialogs/common/info-dialog/info-dialog";
import Table, { IColumnDefinition } from "components/shared-ui/table/table";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { commonActions } from "redux/actions/common";
import { IAppState } from "redux/reducers";
import { CONFIG } from "../../../../config/config";
import styles from "./access-codes-generated-dialog.module.scss";
import { IIAMCredential } from "api/entities/iam-credentials";

const mapDispatchToProps = (
  dispatch: ThunkDispatch<IAppState, void, Action>
) => {
  return {
    startLoading: () => dispatch(commonActions.startLoading()),
    stopLoading: () => dispatch(commonActions.stopLoading()),
  };
};

export interface IAccessCodesGeneratedDialogProps
  extends ReturnType<typeof mapDispatchToProps> {
  open: boolean;
  applicationCredentials: IIAMCredential;
  url?: string;
  onClose: () => void;
}

export const AccessCodesGeneratedDialog = connect(
  null,
  mapDispatchToProps
)((props: IAccessCodesGeneratedDialogProps) => {
  const {
    open,
    applicationCredentials,
  } = props;
  const [isAccessSecretHidden, setIsAccessSecretHidden] =
    useState<boolean>(true);

  const [isArchiveDownloaded, setArchiveDownloaded] = useState<boolean>(false);
  const [isDownloadButtonDisabled, setDownloadButtonDisabled] =
    useState<boolean>(true);

  const columns: IColumnDefinition<IIAMCredential, string>[] = [
    {
      label: "Access Key",
      className: styles["col--access-key"],
      formatter: (row) => row.AccessKeyId,
    },
    {
      label: "Access Secret Key",
      className: styles["col--access-secret"],
      formatter: (row) => (
        <>
          <span>
            {!isAccessSecretHidden ? row.SecretAccessKey : "*".repeat(5)}
          </span>

          <Button
            testId="show-hide-button"
            colors="link"
            onClick={() => setIsAccessSecretHidden(!isAccessSecretHidden)}
          >
            {!isAccessSecretHidden ? "Hide" : "Show"}
          </Button>
        </>
      ),
    },
  ];

  if (props.url) {
    columns.unshift({
      label: "Endpoint URL",
      className: styles["col--endpoint-url"],
      formatter: (_) => props.url,
    });
  }
  function getDownloadLink() {
    return `data:application/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(applicationCredentials)
    )}`;
  }

  function downloadArchive(fileName: string) {
    props.startLoading();

    axios({
      url: getDownloadLink(),
      method: "GET",
      responseType: "blob",
      onDownloadProgress: (e) => {
        if (e.loaded === e.total) {
          props.stopLoading();
        }
      },
    }).then((response) => {
      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      setArchiveDownloaded(true);
      props.stopLoading();
    });
  }

  function onDownloadBtnClick() {
    downloadArchive(
      `${applicationCredentials.UserName ?? "cortex"}_access_credentials.json`
    );
  }

  useEffect(() => {
    if (!!open && !!applicationCredentials) {
      setDownloadButtonDisabled(false);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <InfoDialog
      testId="access-codes-generated"
      title="Access Credentials are successfully created"
      showOkButton={true}
      okCloseButtonsDisabled={!!applicationCredentials && !isArchiveDownloaded}
      open={open}
      onClose={() => {
        if (isArchiveDownloaded) {
          setIsAccessSecretHidden(true);
          setArchiveDownloaded(false);
          setDownloadButtonDisabled(true);
          props.onClose();
        }
        props.onClose();
      }}
    >
      <div className={styles.content}>
        {!!props.applicationCredentials && (
          <>
            <Button
              disabled={isDownloadButtonDisabled}
              testId="download-access-codes-button"
              colors="primary"
              className={styles["btn-access-codes-download"]}
              onClick={() => onDownloadBtnClick()}
            >
              <FileDownloadIcon />
              <span>Download</span>
            </Button>

            <Table
              testId={"access-codes-table"}
              hideCheckbox={true}
              columns={columns}
              rows={[props.applicationCredentials]}
              compact={true}
              requestParams={{}}
              hidePaging={true}
            />

            <div className={styles["warning-message"]}>
              If you lose or forget your secret key, you cannot retrieve it.
              Please keep the credentials in the secure place.
            </div>

            <div className={styles["message--description"]}>
              For guidance on deploying Myota Cortex, visit
              our&nbsp;
              <Link href={CONFIG.instructionPageUrl} target="_blank">
                instruction page
              </Link>
              {/* &nbsp;with the configuration file you downloaded. If you have
              questions or need assistance with Myota managed bZs service,
              contact our support team at <Link>support@myota.io</Link>. */}
            </div>
          </>
        )}
      </div>
    </InfoDialog>
  );
});
